import { Link } from 'react-router-dom';
import {
  PenTool, Type, FileText, Minus, BookOpen, Hash,
  ChevronRight, Sparkles, Italic, User
} from 'lucide-react';

export default function Tools() {
  const tools = [
    {
      id: 'cursive',
      name: 'Cursive Handwriting',
      description: 'Create beautiful cursive practice pages with elegant flowing fonts. Learn connected letter writing and improve penmanship skills.',
      icon: Italic,
      bg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      features: ['Professional cursive fonts', 'Connected letter practice', 'Instant PDF download']
    },
    {
      id: 'print',
      name: 'Print Handwriting',
      description: 'Generate professional print practice sheets with structured lines and quality fonts. Perfect for developing clear writing skills.',
      icon: Type,
      bg: 'bg-bastion-blue-50',
      iconColor: 'text-bastion-blue-500',
      features: ['Quality fonts', 'Structured practice lines', 'Professional printables']
    },
    {
      id: 'name-tracing',
      name: 'Name Tracing',
      description: 'Create personalized name tracing pages with multiple font options. Help children learn to write their names with confidence.',
      icon: User,
      bg: 'bg-green-100',
      iconColor: 'text-green-600',
      features: ['Custom name pages', 'Multiple font options', 'Personalized learning']
    },
    {
      id: 'tracing-lines',
      name: 'Tracing Lines',
      description: 'Perfect first step for pre-writing skills. Generate activities with straight, curved, and dotted lines for pen control practice.',
      icon: Minus,
      bg: 'bg-bastion-orange-50',
      iconColor: 'text-bastion-orange-500',
      features: ['Pre-writing skill development', 'Essential pen control', 'Fine motor skills practice']
    },
    {
      id: 'alphabet-tracing',
      name: 'Alphabet Tracing',
      description: 'Build strong letter formation skills with customizable alphabet exercises. Features uppercase and lowercase letters for foundational practice.',
      icon: BookOpen,
      bg: 'bg-pink-100',
      iconColor: 'text-pink-600',
      features: ['A-Z letter practice', 'Uppercase & lowercase', 'Letter formation guides']
    },
    {
      id: 'number-tracing',
      name: 'Number Tracing',
      description: 'Master number writing with guided practice pages. Features numbers 0-9 and step-by-step difficulty levels for effective math learning.',
      icon: Hash,
      bg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      features: ['Numbers 0-9 practice', 'Math education support', 'Step-by-step difficulty']
    }
  ];

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-bastion-blue-50 text-bastion-blue-500 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            All Handwriting Tools
          </div>
          <h1 className="section-title">Free Handwriting Worksheets & Generators</h1>
          <p className="section-subtitle max-w-2xl mx-auto">
            Create unlimited printable PDFs with instant download. Perfect for teachers,
            parents, and students. Quality fonts, custom content, and easy-to-use tools.
          </p>
        </div>

        {/* Feature badges */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <span className="flex items-center gap-2 bg-white border border-bastion-gray-200 px-4 py-2 rounded-full text-sm">
            <FileText className="w-4 h-4 text-bastion-black" />
            One-click printable PDF
          </span>
          <span className="flex items-center gap-2 bg-white border border-bastion-gray-200 px-4 py-2 rounded-full text-sm">
            <PenTool className="w-4 h-4 text-bastion-black" />
            Pen-specific options
          </span>
          <span className="flex items-center gap-2 bg-white border border-bastion-gray-200 px-4 py-2 rounded-full text-sm">
            <Sparkles className="w-4 h-4 text-bastion-black" />
            No signup required
          </span>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((tool) => (
            <Link
              key={tool.id}
              to={`/tools/${tool.id}`}
              className="card-hover group"
            >
              <div className={`w-14 h-14 ${tool.bg} rounded-xl flex items-center justify-center mb-4`}>
                <tool.icon className={`w-7 h-7 ${tool.iconColor}`} />
              </div>

              <h2 className="text-xl font-semibold text-bastion-black mb-2 group-hover:text-bastion-black transition-colors">
                {tool.name}
              </h2>

              <p className="text-bastion-gray-800 mb-4">
                {tool.description}
              </p>

              <ul className="space-y-2 mb-4">
                {tool.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-bastion-gray-700 font-medium">
                    <div className="w-1.5 h-1.5 bg-bastion-black rounded-full" />
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-2 text-bastion-black font-medium">
                Create Worksheet
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
